import { GetStaticProps } from 'next'
import Image from 'next/image'
import styled from 'styled-components'
import tw from 'tailwind-styled-components'
import Head from 'next/head'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NavBar, FooterLanding, Button, AboutUsList } from '@foros-fe/core'
import { EmployeesDocument } from '@foros-fe/graphql'
import { domains } from '@foros-fe/core/config'
import TreesVideo from '@foros-fe/assets/videos/trees-about.mp4'
import TeamPhoto from '@foros-fe/assets/images/team-photo.png'
import Squirrel from '@foros-fe/assets/images/squirrel.png'
import { fetchSSRQuery } from '@/utils/apolloClient'
import IronWolfCapitalPng from '@foros-fe/assets/icons/iron-wolf-capital-logo.png'
import SeventyVenturesPng from '@foros-fe/assets/icons/70-ventures-logo.png'
import CoinvestCapitalPng from '@foros-fe/assets/icons/coinvest-capital-logo.png'

const Col = tw.div`flex flex-col`
const Container = tw(Col)`container py-12 md:py-16`
const VideoContainer = tw.div`relative bg-gray-200 w-screen h-[600px]`
const Video = tw.video`absolute h-full w-full object-cover top-0`
const VideoTitleContainer = tw(Col)`
  relative
  top-0
  w-full
  h-full
  container
  items-center
  justify-center
  z-20
`
const Overlay = tw.div`absolute top-0 bottom-0 left-0 right-0 bg-black z-10 opacity-25`
const Text = tw.p`text-base md:text-lg leading-6 md:leading-7 text-center lg:max-w-[850px]`
const JoinTitle = tw.span`text-3xl md:text-4xl mb-8 font-semibold`
const Title = tw(JoinTitle)`text-center`
const TeamDescContainer = tw(Col)`items-center text-center mb-6 lg:mb-10`
// Fire Laurynas for not updating style guide :D
const JoinContainer = tw.div`
  rounded-lg
  bg-[#F8F6F3]
  relative
  flex
  h-auto
  md:min-h-[300px]
  overflow-hidden
  items-center
  lg:max-w-[1064px]
  lg:self-center
`
const InfoText = tw.p`text-gray-800`
const TeamImgContainer = styled.div`
  border-top-left-radius: 50%;
  border-bottom-left-radius: 50%;
  overflow: hidden;
  margin-right: -70px;
  height: 400px;
  width: 400px;
`
const RoundImgContainer = tw.div`relative mr-10 w-[144px] h-[144px]`
const Circle = tw.div`
  absolute
  w-full
  h-full
  rounded-full
  border-dashed
  border-2
  top-2
  left-2
  border-secondary-500
  bg-transparent
`
const InvestBlock = tw.div`w-screen bg-[#F8F6F3]`
const InvestImgContainer = tw(Col)`
  md:flex-row
  self-stretch
  justify-between
  items-center
`

const email = domains.current.contacts.email

export default function AboutUs() {
  const { t } = useTranslation('about')

  return (
    <>
      <Head>
        <title>{t('pageTitle')}</title>
      </Head>
      <main>
        <NavBar />
        <VideoContainer>
          <Overlay />
          <Video autoPlay loop muted>
            <source src={TreesVideo} type='video/mp4' />
          </Video>
          <VideoTitleContainer>
            <Title className='text-white'>{t('videoTitle')}</Title>
            <Text className='text-white text-medium'>{t('videoText')}</Text>
          </VideoTitleContainer>
        </VideoContainer>
        <Container>
          <TeamDescContainer>
            <Title className='text-black'>{t('teamTitle')}</Title>
            <Text className='text-gray-800 '>{t('teamText')} </Text>
          </TeamDescContainer>
        </Container>
        <AboutUsList />
        <Container>
          <JoinContainer>
            <Col className='px-11 py-11 flex-1 items-center md:items-start text-center md:text-left'>
              <JoinTitle className='mb-2'>{t('growWithUsTitle')}</JoinTitle>
              <InfoText className='mb-8'>{t('growWithUsText')} </InfoText>
              <a href={`mailto:${email}`} target='_blank' rel='noreferrer'>
                <Button
                  title={t('joinTheTeam')}
                  value='join-the-team'
                  className='self-center md:self-start'
                />
              </a>
            </Col>
            <RoundImgContainer className='hidden lg:block'>
              <Image src={Squirrel} className='rounded-full' />
              <Circle />
            </RoundImgContainer>
            <TeamImgContainer className='hidden md:flex'>
              <Image src={TeamPhoto} />
            </TeamImgContainer>
          </JoinContainer>
        </Container>
        <InvestBlock>
          <Container className='items-center'>
            <Title className='text-gray-800'>{t('ourInvestorsTitle')}</Title>
            <Text className='text-gray-800 text-center mb-8 lg:mb-10'>{t('investorText')}</Text>
            <InvestImgContainer>
              <div className='w-[245px] lg:w-[301px] mb-5 md:mb-0'>
                <Image src={IronWolfCapitalPng} />
              </div>
              <div className='w-[195px] lg:w-[240px] mb-11 md:mb-0'>
                <Image src={SeventyVenturesPng} />
              </div>
              <div className='w-[218px] lg:w-[253px]'>
                <Image src={CoinvestCapitalPng} />
              </div>
            </InvestImgContainer>
          </Container>
        </InvestBlock>
        <FooterLanding />
      </main>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const [apolloProps, translationProps] = await Promise.all([
    fetchSSRQuery({ query: EmployeesDocument }),
    serverSideTranslations(locale!, ['common', 'about', 'enum']),
  ])

  return {
    props: {
      ssr: true,
      ...apolloProps,
      ...translationProps,
    },
    revalidate: 60 * 60,
  }
}
